import { isAxiosError, isCancel } from 'axios';
import Decimal from 'decimal.js';

import type { PerpsPositionSide, PerpsSocialFinancialValue } from 'types/perpsSocial';

const RETRY_DELAYS_MS = [1_000, 2_000, 4_000] as const;

export const PERPS_SOCIAL_STALE_TIME_MS = 60_000;

export function normalizePerpsSocialTicker(ticker: string): string {
    return ticker.trim().toUpperCase();
}

export function normalizePerpsPositionSide(value: unknown): PerpsPositionSide | null {
    if (typeof value !== 'string') return null;

    switch (value.trim().toLowerCase()) {
        case 'yes':
        case 'long':
            return 'long';
        case 'no':
        case 'short':
            return 'short';
        default:
            return null;
    }
}

export function toFinancialString(value: PerpsSocialFinancialValue): string | null {
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
    if (typeof value !== 'string') return null;

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

export function centicentsToCentsString(value: PerpsSocialFinancialValue): string | null {
    const normalized = toFinancialString(value);
    if (normalized === null) return null;

    try {
        // Decimal's default precision is 20 significant digits. API values may
        // exceed that, so use an input-sized clone rather than rounding during
        // the centicents-to-cents conversion.
        const significantDigits = normalized.replace(/[^0-9]/g, '').length;
        const PreciseDecimal = Decimal.clone({ precision: Math.max(40, significantDigits + 2) });
        const decimal = new PreciseDecimal(normalized);
        return decimal.isFinite() ? decimal.dividedBy(100).toString() : null;
    } catch {
        return null;
    }
}

export function perpsSocialRetryDelay(attemptIndex: number): number {
    return RETRY_DELAYS_MS[Math.min(Math.max(attemptIndex, 0), RETRY_DELAYS_MS.length - 1)];
}

/**
 * Match mobile's retry policy: retry transport failures, HTTP 429s, and 5xx
 * responses three times. Cancellations and deterministic 4xx responses stop
 * immediately.
 */
export function shouldRetryPerpsSocialQuery(failureCount: number, error: unknown): boolean {
    if (failureCount >= RETRY_DELAYS_MS.length || isCancel(error)) return false;
    if (error instanceof DOMException && error.name === 'AbortError') return false;
    if (!isAxiosError(error)) return false;

    const status = error.response?.status;
    return status === undefined || status === 429 || status >= 500;
}
